"use strict";
/**
 * ==================================================================================
 * Description:       For use in data-list and for sharing with home-dashboard.
 *                    This entire logic flow is done through JQuery (No React),
 *                    but we borrow concepts from React components like state
 *                    (state_obj) and props (props_obj)
 * Creation Date:     12/06/16
 * Author:            AMcDaniel
 * ==================================================================================
 */

var pivot_chart_utils = {
    /**
     * Gets the specific attributes used to build dxPivotGrid columns
     * @param entity_name
     * @param array_of_force_expanded_ref_fields - Usually comes from (is a field in) entity_attributes
     * @param error_function
     * @param callback - Usually includes a call to initialize_pivot_grid_sync, along with the required jquery selectors, etc
     */
    get_initial_pivot_grid_attributes: function (entity_name, array_of_force_expanded_ref_fields, error_function, callback) {
        var request = {};
        request.entity = "app_object";
        request.method = "get_by_code";
        request.data = {app_object_code: entity_name + "_default_data_list", force_data_list_nested_captions: true, array_of_force_expanded_ref_fields: array_of_force_expanded_ref_fields};
        do_authenticated_http_call({
            method: "POST",
            url: "/api/entity/invoke_method",
            contentType: "application/json",
            dataType: 'json',
            data: JSON.stringify(request),
            success: function (data) {
                return callback({
                    entity_attributes: data.data.entity_attributes
                });
            },
            error: error_function
        });
    },
    /**
     * Initializes the dxPivotGrid with no further interaction required, however it is not truly sync, as the init_completed_function allows a callback (typically used to hide the loading screen)

     * @param props_obj - Has the following fields:
     chart_section_jquery_selector - Jquery selector for the element which will contain the graph/dxChart,
     controls_section_jquery_selector - Jquery selector for the panel which will contain the custom controls for the dxPivotGrid & Chart,
     pivot_grid_section_jquery_selector - Jquery selector for the panel which will contain the dxPivotGrid component,
     load_panel_div_selector - Jquery selector for a div to be used for a loading panel,
     tooltip_div_selector - Jquery selector for a div to be used for a mouseover tooltip for the chart,
     mouse_y_offset - A Number, in pixels, to shift the tooltip popup,
     mouse_x_offset - A Number, in pixels, to shift the tooltip popup,
     app_object_handler - Navigation handler for the drilldown,
     export_grid_conditions - app_object conditions which restrict all data in the pivotGrid (and all drilldowns as part of drilldown_conditions),
     app_object_conditions - app_object conditions which restrict all data in the data-list, not applied to pivotGrid,
     entity_attributes - received from get_initial_pivot_grid_attributes,
     update_height_function - A function f(offset_pixels, return_result_as_number) used to get the height of the view area (as a Number or as a string with "px"),
     add_save_option - If true, adds a save button to the control panel,
     error_function - Handles error callback from invoke_method
     hide_pivot_grid_component_initially - If true, hides the pivot grid, making the chart fill the entire available space,
     add_delete_option - If true, allows Dashboard part to be deleted (requires dashboard_part prop),
     dashboard_part - (optional) Contains data from the dashboard part that holds this component - namely requires .code and .aggregation_code,
     remove_component_function - Function which visually removes containing component from view
     session_storage_code - (optional) Will store state to sessionStorage if this is supplied (usually from data-list)
     array_of_force_expanded_ref_fields - Comes from entity_attributes.  Allows certain fields to perform Joins on the aggregation data
     * @param init_grid_columns_properties - Properties to be passed into the init_grid_columns function and used as properties for the shared_model_utils.create_data_list_column function
     * @param chart_name - Used for Dashboard Parts as a title
     * @param chart_height - The desired initial height of the chart (as a Number),
     * @param init_completed_function - Callback after components are ready
     * @param initial_data_source - For dashboard parts which need to initialize and graph immediately
     * @param preset_columns_obj - Same as above
     * @param skip_initial_data_source_processing - set to true if the aggregation data is unprocessed (usually from data-list sessionStorage)
     * @returns {*}
     */
    initialize_pivot_grid_sync: function (props_obj, init_grid_columns_properties, chart_name, chart_height, init_completed_function, initial_data_source, preset_columns_obj, skip_initial_data_source_processing) {
        var state_obj = {
            entity_ref_dictionary: {},
            field_path_values_fetched_dictionary: {},
            chart_height: chart_height,
            data_filter_values: [],
            date_fields_array_for_bug_workaround: []
        }; //Doesn't need to be given as a parameter
        state_obj.componentWillUnmount = pivot_chart_utils.componentWillUnmount.bind(state_obj, state_obj);
        state_obj.entity_instances = [];
        var initial_data_source_holder = {
            initial_data_source: initial_data_source
        };


        state_obj.dx_load_panel = props_obj.load_panel_div_selector.dxLoadPanel({
            message: 'Loading filter data...',
            showIndicator: true,
            visible: false
        }).dxLoadPanel("instance"); //initialize loading message hidden


        props_obj.tooltip_div_selector.css({'position': 'absolute'});
        props_obj.chart_section_jquery_selector.mousemove(function(e){
            props_obj.tooltip_div_selector.css({'top': Math.max(e.pageY - props_obj.mouse_y_offset, 10), 'left': Math.max(e.pageX - props_obj.mouse_x_offset, 10)});
        });

        state_obj.dx_tooltip = props_obj.tooltip_div_selector.dxTooltip({
            target: props_obj.chart_section_jquery_selector,
            visible: false
        }).dxTooltip("instance"); //initialize tooltip popup hidden


        pivot_chart_utils.init_grid_columns(state_obj, props_obj, init_grid_columns_properties, preset_columns_obj, initial_data_source_holder, skip_initial_data_source_processing);
        pivot_chart_utils.init_grid(state_obj, props_obj, chart_name, init_completed_function, initial_data_source_holder);
        return state_obj;
    },
    /**
     * Similar to similarly named functions found in other files.  Prepares columns for use in Pivot Grid
     * @param state_obj
     * @param props_obj
     * @param properties
     * @param preset_columns_obj
     * @param initial_data_source_holder
     * @param skip_initial_data_source_processing
     */
    init_grid_columns: function(state_obj, props_obj, properties, preset_columns_obj, initial_data_source_holder, skip_initial_data_source_processing){
        var columns = [];
        var attributes = props_obj.entity_attributes.attributes || [];
        var initial_data_source = null;
        if(initial_data_source_holder) {
            initial_data_source = initial_data_source_holder.initial_data_source;
        }
        properties = shallow_copy(properties); //just to not mess up the object:
        properties.disallow_editing = true;
        properties.disallow_null_captions = true;

        for (var i = 0; i < attributes.length; i++) {
            var attribute = attributes[i];
            if ((attribute.type == "Mixed") || attribute.is_array || (attribute.is_nested_entity && attribute.data_is_nested)) {
                continue;
            }
            // We first check the "list_visible" property of the "original_attributes" because it can be overridden in the "attributes" list
            if(attribute.list_visible || attribute.form_visible) { //fields have to be visible somewhere to be filterable
                properties.attribute = attribute;
                var column = create_data_list_column(properties);
                if (column) {
                    pivot_chart_utils.add_column_customize_text_and_selector_values(state_obj, props_obj, attribute, column);

                    if (attribute.db_type == "Date") {
                        state_obj.date_fields_array_for_bug_workaround.push(column.dataField);
                    }

                    if(column.dataType == 'number') {
                        column.isMeasure = true;
                        column.summaryType = "sum";
                    }
                    else {
                        column.isMeasure = false;
                    }

                    column.visible = (attribute.list_visible || attribute.form_visible);
                    columns.push(column);
                }
            }
        }

        var record_count_column = {name: "__record_count", dataField: "__record_count", caption: R.custom_aggregation_caption_record_count || "Count", visible: true, isMeasure:true, summaryType: "sum"};
        columns.push(record_count_column);

        state_obj.columns = columns;
        state_obj.columns_dictionary = general_utils.convert_to_dictionary_by_field_names(columns, {key: "dataField"});
        if(preset_columns_obj) {
            initial_data_source_holder.initial_data_source = pivot_chart_utils.initialize_pivot_grid_columns_and_data(state_obj, props_obj, initial_data_source, preset_columns_obj.chart_color_keys, preset_columns_obj.x_axis_fields, preset_columns_obj.y_axis_fields, preset_columns_obj.applied_filters, false, !skip_initial_data_source_processing, false, false);
        }
    },
    /**
     * Initializes grid properties and instantiates dxChart, dxPivotGrid, and custom controls.
     * @param state_obj
     * @param props_obj
     * @param chart_name
     * @param init_completed_function
     * @param initial_data_source_holder
     */
    init_grid: function (state_obj, props_obj, chart_name, init_completed_function, initial_data_source_holder) {
        var initial_data_source = null;
        if(initial_data_source_holder) {
            initial_data_source = initial_data_source_holder.initial_data_source;
        }
        initial_data_source = initial_data_source || [];
        var grid_properties = {
            allowFiltering: true,
            fieldChooser: {
                enabled: true
            },
            export: {
                enabled: true,
                fileName: chart_name + " " + "Pivot Grid"
            },
            visible: !props_obj.hide_pivot_grid_component_initially,
            stateStoring: {
                enabled: true,
                savingTimeout: 0,
                storageKey: null,
                type: "custom",
                customSave: function (dx_pivot_grid_state) { //This event throws often, but is the only event I've found that throws on fields being moved around in dxPivotGridFieldChooser
                    var exported_components = null;
                    if (dx_pivot_grid_state.fields) {
                        exported_components = dx_pivot_grid_state.fields.filter(function (a) {
                            return (a.area && (a.area != "") && (a.areaIndex >= 0)); //for some reason -1 is used when things are removed from a section
                        });
                        if (!exported_components.length) {
                            exported_components = null;
                        }
                        else {
                            pivot_chart_utils.fetch_fields_chooser_data_filter_values_and_update_data_source(state_obj, props_obj, exported_components);
                            pivot_chart_utils.add_fields_chooser_data_area_aggregation_type_selectors(state_obj, exported_components.filter(function(a) {return ((a.area == "data") && (a.areaIndex >= 0));}));
                        }
                    }
                    state_obj.last_exported_components = exported_components;
                },
                customLoad: function (e) {
                    var d = new $.Deferred();
                    d.reject(new Error("No data to load"));
                    return d.promise();
                }
            },
            height: function () {
                var a = props_obj.update_height_function(state_obj.chart_height);
                return a;
            },
            width: "100%"
        };
        grid_properties.dataSource = {
            fields: state_obj.columns,
            store: initial_data_source //Only allow data to come in pre-aggregated format
        };

        var run_once = false;
        grid_properties.onContentReady = function () {
            if(!run_once) {
                run_once = true;
                pivot_chart_utils.add_onclick_event_for_field_chooser_launch(state_obj, props_obj);
                init_completed_function();
                init_completed_function = function () {};//don't call this again
                state_obj.grid_properties.onContentReady = init_completed_function; //For future instantiations
            }
        };


        state_obj.grid_properties = grid_properties;
        state_obj.pivot_chart_properties = {
            dataFieldsDisplayMode: "splitAxes", alternateDataFields: false, processCell: function (e) {
                var conditions_object = {};
                pivot_chart_utils.add_to_conditions_object(state_obj, props_obj, conditions_object, e.columnFields, e.columnPath);
                pivot_chart_utils.add_to_conditions_object(state_obj, props_obj, conditions_object, e.rowFields, e.rowPath);

                e.chartDataItem.tag = {conditions: conditions_object}; //Storing it here just for a convenient way of passing this to the corresponding Point on the chart.
            }
        };
        state_obj.pivot_chart_options = {
            dataSource: initial_data_source,
            commonSeriesSettings: {
                type: "bar"
            },
            size: {
                height: (props_obj.hide_pivot_grid_component_initially) ? props_obj.update_height_function(0, true) : state_obj.chart_height
            },
            adaptiveLayout: {
                keepLabels: false
            },
            onPointHoverChanged: function (info) {
                var point = info.target;
                if(point.isHovered()) {
                    var series = point.series;
                    if(series) {
                        state_obj.last_tooltip_text = series.name;
                        state_obj.dx_tooltip.option("contentTemplate", function (contentElement) {
                            if(state_obj.last_tooltip_text != null) {
                                contentElement.text(state_obj.last_tooltip_text);
                            }
                        });
                        state_obj.dx_tooltip.show(props_obj.tooltip_div_selector); //show takes an optional target
                    }
                }
            },
            onPointClick: function (e) {
                var conditions_holder = e.target.tag;
                if (conditions_holder) {
                    var app_object = {};
                    app_object.type = "data_list";
                    app_object.code = props_obj.entity_attributes.entity + "_default_data_list";
                    app_object.drilldown_conditions = shared_model_utils.add_and_clause_to_conditions(conditions_holder.conditions, props_obj.export_grid_conditions);

                    if(props_obj.app_object_conditions) {
                        app_object.conditions = props_obj.app_object_conditions;
                    }

                    return props_obj.app_object_handler(app_object);
                }
            }
        };

        //Insert custom controls:
        //pivot_chart_utils.insert_pie_chart_button(state_obj, props_obj);
        pivot_chart_utils.insert_chart_type_select_list(state_obj, props_obj);
        if(props_obj.add_save_option) {
            pivot_chart_utils.insert_create_aggregation_button(state_obj, props_obj);
        }
        if(props_obj.add_delete_option) {
            pivot_chart_utils.insert_remove_aggregation_button(state_obj, props_obj);
        }
        pivot_chart_utils.insert_show_hide_button(state_obj, props_obj);

        state_obj.pivot_grid_chart = props_obj.chart_section_jquery_selector.dxChart(state_obj.pivot_chart_options).dxChart("instance");
        state_obj.pivot_grid = props_obj.pivot_grid_section_jquery_selector.dxPivotGrid(state_obj.grid_properties).dxPivotGrid("instance");
        state_obj.unbind_pivot_grid_chart_function = state_obj.pivot_grid.bindChart(state_obj.pivot_grid_chart, state_obj.pivot_chart_properties);
    },
    /**
     * Used in creating drilldown conditions for the pivot grid chart
     * @param state_obj
     * @param props_obj
     * @param conditions_obj
     * @param field_names_array
     * @param field_values_array
     * @returns {*}
     */
    add_to_conditions_object: function(state_obj, props_obj, conditions_obj, field_names_array, field_values_array) {
        if(field_values_array && field_values_array.length) {
            var field_paths = props_obj.entity_attributes.field_paths || {};
            var last_year_condition, most_strict_date_condition;
            for (var i = 0; (i < field_values_array.length); i++) {
                var field_obj = field_names_array[i];
                if(field_obj) {
                    var attribute = field_paths[field_obj.dataField];
                    if(field_values_array[i] && field_obj.dataType == "date") {
                        var gte_date_value, lt_date_value;
                        switch(field_obj.groupInterval) {
                            case "year":
                                var given_year_as_number = field_values_array[i];
                                last_year_condition = given_year_as_number;
                                lt_date_value = new Date(last_year_condition + 1, 0, 1);
                                gte_date_value = new Date(last_year_condition, 0, 1);
                                break;
                            case "quarter":
                                var given_quarter_as_number = field_values_array[i];
                                lt_date_value = new Date(last_year_condition, (given_quarter_as_number * 3), 1);
                                gte_date_value = new Date(last_year_condition, (given_quarter_as_number * 3) - 3, 1);
                                break;
                            case "month": //default group by Month
                            default:
                                var given_month_as_number = field_values_array[i];
                                lt_date_value = new Date(last_year_condition, given_month_as_number, 1);
                                gte_date_value = new Date(last_year_condition, given_month_as_number - 1, 1);
                                break;
                        }
                        most_strict_date_condition = {condition: {"$gte": gte_date_value, "$lt": lt_date_value}};
                        most_strict_date_condition.field = field_obj.dataField;
                    }
                    else {
                        if (most_strict_date_condition) { //handle the last most_strict_date_condition leftover from the Date field
                            conditions_obj[most_strict_date_condition.field] = most_strict_date_condition.condition;
                            most_strict_date_condition = null;
                        }

                        if (field_values_array[i] == null) {
                            conditions_obj[field_obj.dataField] = {$eq: null};
                        }
                        else { //Should be a straightforward value match
                            conditions_obj[field_obj.dataField] = field_values_array[i];
                        }
                    }
                }
            }
            if(most_strict_date_condition) { //handle the last most_strict_date_condition leftover from the Date field
                conditions_obj[most_strict_date_condition.field] = most_strict_date_condition.condition;
                most_strict_date_condition = null;
            }
        }
        return conditions_obj;
    },
    /**
     * Shows/hides the dxPivotGrid (Table)
     * @param state_obj
     * @param props_obj
     */
    insert_show_hide_button: function(state_obj, props_obj){
        props_obj.controls_section_jquery_selector.each(function () {
            if ($(this).children('div[data-show-hide-pivot-grid-button="true"]').length == 0) {
                var tmpDiv = $("<div id='show-hide-pivot-grid-button' data-show-hide-pivot-grid-button='true'/>");
                state_obj.dx_pivot_show_hide_button = tmpDiv.dxButton({
                    icon: "fa fa-sliders",
                    hint: R.show_hide_pivot_grid_button_hint,
                    onClick: function() {
                        var current_visibility = state_obj.pivot_grid.option("visible");
                        state_obj.pivot_grid.option("visible", !current_visibility);
                        if(current_visibility) {
                            state_obj.pivot_grid_chart.option("size.height", props_obj.update_height_function(0, true));
                        }
                        else {
                            state_obj.pivot_grid_chart.option("size.height", state_obj.chart_height);
                            state_obj.pivot_grid.repaint(); //calls update_height_function
                        }
                    }
                }).dxButton("instance");
                $(this).prepend(tmpDiv);
            }
        });
    },
    /**
     * Currently commented out.  Would allow a pie-chart display type for simple data.
     * @param state_obj
     * @param props_obj
     */
    insert_pie_chart_button: function(state_obj, props_obj){
        props_obj.controls_section_jquery_selector.each(function () {
            if ($(this).children('div[data-pie-chart-pivot-grid-button="true"]').length == 0) {
                var tmpDiv = $("<div id='pie-chart-pivot-grid-button' data-pie-chart-pivot-grid-button='true'/>");
                tmpDiv.dxButton({
                    icon: "fa fa-pie-chart",
                    hint: R.pie_chart_pivot_grid_button_hint,
                    onClick: function() {return pivot_grid_pie_chart_utils.create_pie_chart_display(state_obj, props_obj);}
                }).dxButton("instance");
                $(this).prepend(tmpDiv);
            }
        });
    },
    //All dxChart supported types
    dx_chart_types: [{caption: R.label_chart_styles_bar || "Bar", value: 'bar'}, {caption: R.label_chart_styles_line || "Line", value: 'line'}, {caption: R.label_chart_styles_area || "Area", value: 'area'}, {caption: R.label_chart_styles_spline || "Spline", value: 'spline'}, {caption: R.label_chart_styles_scatter || "Scatter", value: 'scatter'}, {caption: R.label_chart_styles_candlestick || "Candlestick", value: 'candlestick'}, {caption: R.label_chart_styles_stock || "Stock", value: 'stock'}, {caption: R.label_chart_styles_bubble || "Bubble", value: 'bubble'},
        {caption: R.label_chart_styles_stackedline || "Stacked Line", value: 'stackedline'}, {caption: R.label_chart_styles_fullstackedline || "Full Stacked Line", value: 'fullstackedline'}, {caption: R.label_chart_styles_stackedarea || "Stacked Area", value: 'stackedarea'}, {caption: R.label_chart_styles_fullstackedarea || "Full Stacked Area", value: 'fullstackedarea'}, {caption: R.label_chart_styles_stackedbar || "Stacked Bar", value: 'stackedbar'}, {caption: R.label_chart_styles_fullstackedbar || "Full Stacked Bar", value: 'fullstackedbar'},
        {caption: R.label_chart_styles_splinearea || "Spline Area", value: 'splinearea'}, {caption: R.label_chart_styles_rangearea || "Range Area", value: 'rangearea'}, {caption: R.label_chart_styles_rangebar || "Range Bar", value: 'rangebar'}, {caption: R.label_chart_styles_stepline || "Step Line", value: 'stepline'}, {caption: R.label_chart_styles_steparea || "Step Area", value: 'steparea'}, {caption: R.label_chart_styles_fullstackedspline || "Full Stacked Spline", value: 'fullstackedspline'}, {caption: R.label_chart_styles_stackedspline || "Stacked Spline", value: 'stackedspline'}, {caption: R.label_chart_styles_stackedsplinearea || "Stacked Spline Area", value: 'stackedsplinearea'}, {caption: R.label_chart_styles_fullstackedsplinearea || "Full Stacked Spline Area", value: 'fullstackedsplinearea'}
    ],
    /**
     * Allows selection of dxChart type (See types variable in function)
     * @param state_obj
     * @param props_obj
     */
    insert_chart_type_select_list: function(state_obj, props_obj){
        props_obj.controls_section_jquery_selector.each(function () {
            if ($(this).children('div[data-change-chart-type-pivot-grid="true"]').length == 0) {
                var tmpDiv = $("<div id='change-chart-type-pivot-grid' data-change-chart-type-pivot-grid='true' style='display:inline-block; vertical-align: top; margin-left: 20px'/>");
                tmpDiv.dxSelectBox({
                    dataSource: pivot_chart_utils.dx_chart_types,
                    displayExpr: "caption",
                    valueExpr: "value",
                    value: pivot_chart_utils.dx_chart_types[0].value,
                    height: "35px",
                    width: "175px",
                    onValueChanged: function (e) {
                        pivot_chart_utils.switch_chart_type(state_obj, e.value);
                    }
                });
                $(this).prepend(tmpDiv);
            }
        });
    },
    /**
     * Currently commented out.  This button allows saving of the dxPivotGrid to the dashboard
     * @param state_obj
     * @param props_obj
     */
    insert_create_aggregation_button: function(state_obj, props_obj){
        props_obj.controls_section_jquery_selector.each(function () {
            if ($(this).children('div[data-save-aggregation-button="true"]').length == 0) {
                var tmpDiv = $("<div id='save-aggregation-button' data-save-aggregation-button='true' style='margin-left: 3px'/>");
                tmpDiv.dxButton({
                    icon: "fa fa-floppy-o",
                    hint: R.save_aggregation_button_hint,
                    height: '35px',
                    onClick: function() {return pivot_chart_utils.make_aggregation_call_to_server(state_obj, props_obj, false);}
                }).dxButton("instance");
                $(this).prepend(tmpDiv);
            }
        });
    },
    insert_remove_aggregation_button: function(state_obj, props_obj){
        props_obj.controls_section_jquery_selector.each(function () {
            if ($(this).children('div[data-remove-saved-aggregation-button="true"]').length == 0) {
                var tmpDiv = $("<div id='remove-saved-aggregation-button' data-remove-saved-aggregation-button='true' style='margin-left: 3px'/>");
                tmpDiv.dxButton({
                    icon: "fa fa-times",
                    hint: R.delete_aggregation_button_hint || "Remove from Dashboard",
                    height: '35px',
                    onClick: function() {
                        var r = confirm("Press OK to delete this from your dashboard");
                        if (r == true) {
                            return pivot_chart_utils.remove_aggregation_from_dashboard(state_obj, props_obj);
                        }
                    }
                }).dxButton("instance");
                $(this).prepend(tmpDiv);
            }
        });
    },
    remove_aggregation_from_dashboard: function(state_obj, props_obj) {
        var success_func = function (data) {
            Notify(data.message, 'bottom-right', notify_timeout, 'green', 'fa-check', true);
            localStorage.removeItem('home');
            localStorage.removeItem('get_home_layout'); //Refresh Dashboard Parts data
            if(props_obj.remove_component_function) {
                props_obj.remove_component_function();
            }
        };
        var complete_func = function () {
        };
        var send_data = {
            aggregation_code: props_obj.dashboard_part.aggregation_code,
            dashboard_part_code: props_obj.dashboard_part.code
        };
        invoke_method("dashboard_part", "remove_from_dashboard", send_data, success_func, props_obj.error_function, complete_func);
    },
    /**
     * Switches dxChart type option
     * @param state_obj
     * @param new_type
     */
    switch_chart_type: function(state_obj, new_type) {
        state_obj.pivot_grid_chart.option("commonSeriesSettings.type", new_type);
    },
    /**
     * Upon opening the field_chooser, we need to disable dxPivotGrid while the field_chooser is being used, and re-enable it when it closes.  Also want to add an event to closing the field_chooser (for calling server), and upon opening the field_chooser, call add_fields_chooser_data_area_aggregation_type_selectors to Jquery in some options into any fields already in the Data portion of the chooser.
     * @param state_obj
     * @param props_obj
     */
    add_onclick_event_for_field_chooser_launch: function(state_obj, props_obj) {
        props_obj.pivot_grid_section_jquery_selector.find('.dx-pivotgrid-field-chooser-button').click(function () {
            pivot_chart_utils.disable_pivot_grid_updates_and_show_field_chooser(state_obj, props_obj);
        });
    },
    /**
     * Disable (in this case Hide works better) the dxPivotGrid while the field_chooser is being used.  Also want to disable the dxChart (in this case detaching worked better).  We also had to expand the dxChart size as a background canvas to allow the field_chooser to be movable (for some reason it was restricted to the size of the dxChart)
     * @param state_obj
     * @param props_obj
     */
    disable_pivot_grid_updates_and_show_field_chooser: function(state_obj, props_obj) {
        props_obj.controls_section_jquery_selector.hide();
        state_obj.pivot_grid_chart.option("size.height", props_obj.update_height_function(-36, true)); //36 accounts for the height of the controls div
        state_obj.pivot_grid_chart.showLoadingIndicator(); //supposed to prevent this from updating during column choosing
        if (state_obj.unbind_pivot_grid_chart_function) {
            state_obj.unbind_pivot_grid_chart_function(); //unbind chart to prevent updates
            state_obj.unbind_pivot_grid_chart_function = null;
        }
        state_obj.pivot_grid_chart_disabled = true;

        pivot_chart_utils.swap_pivot_grid_aggregation_data_source_with_data_filter_source(state_obj, props_obj, false);
        state_obj.pivot_grid.getFieldChooserPopup().show().then(pivot_chart_utils.do_post_field_chooser_popup_jquery_operations.bind(state_obj, state_obj, props_obj));
        state_obj.pivot_grid.option("visible", false);
    },
    do_post_field_chooser_popup_jquery_operations: function(state_obj, props_obj) {
        if(!state_obj.added_field_chooser_close_onclick_event) {
            state_obj.added_field_chooser_close_onclick_event = true; //Only need to do this once per session
            $(".dx-fieldchooser-popup .dx-closebutton").click(function () {
                pivot_chart_utils.make_aggregation_call_to_server(state_obj, props_obj, true);
            });
        }
        pivot_chart_utils.add_fields_chooser_data_area_aggregation_type_selectors(state_obj, (state_obj.last_exported_components || []).filter(function (a) {
            return ((a.area == "data") && (a.areaIndex >= 0));
        }));
    },
    /**
     * Sets everything back to normal after the field_chooser is closed
     * @param state_obj
     * @param props_obj
     * @param requires_column_reset
     */
    resume_pivot_grid_updates: function(state_obj, props_obj, requires_column_reset) {
        if(state_obj.last_pivot_table_data) {
            var local_state;
            if(!requires_column_reset) {
                //Hack our way around a bug here: Remove all chosen fields from column chooser and close it, then open it back and they were coming back, unless we explicitly restore this state
                local_state = state_obj.pivot_grid.getDataSource().state();
            }
            pivot_chart_utils.initialize_pivot_grid_columns_and_data(state_obj, props_obj, [], state_obj.last_successful_request.exported_rows, state_obj.last_successful_request.exported_columns, state_obj.last_successful_request.exported_data, state_obj.last_successful_request.exported_filters, true, true, true, !requires_column_reset);
            state_obj.pivot_grid.option("dataSource.store", state_obj.last_pivot_table_data);
            if(!requires_column_reset) {
                state_obj.pivot_grid.getDataSource().state(local_state);
            }
            state_obj.unbind_pivot_grid_chart_function = state_obj.pivot_grid.bindChart(state_obj.pivot_grid_chart, state_obj.pivot_chart_properties); //Requires rebind to update with changes
        }
        state_obj.pivot_grid_chart.option("size.height", state_obj.chart_height);
        if (!state_obj.unbind_pivot_grid_chart_function) {
            state_obj.unbind_pivot_grid_chart_function = state_obj.pivot_grid.bindChart(state_obj.pivot_grid_chart, state_obj.pivot_chart_properties); //Rebind, since the normal workflow didn't do it
        }
        state_obj.pivot_grid_chart_disabled = false;
        props_obj.controls_section_jquery_selector.show();
        state_obj.pivot_grid.option("visible", true);
        state_obj.pivot_grid_chart.hideLoadingIndicator();
    },
    /**
     * Used to run an aggregation immediately, or to save the current dxPivotGrid aggregation to the User's Dashboard
     * @param state_obj
     * @param props_obj
     * @param run_immediately - if false, this saves to Dashboard instead
     * @returns {*}
     */
    make_aggregation_call_to_server: function(state_obj, props_obj, run_immediately) {
        var requires_column_reset = false;
        if(state_obj.last_exported_components && state_obj.last_exported_components.length) {
            var aggregation_name;
            var exported_components = state_obj.last_exported_components;
            var last_successful_request = state_obj.last_successful_request;
            var this_request = pivot_chart_utils.make_request_object(exported_components);
            var array_of_new_fields = pivot_chart_utils.filter_pivot_data_grid_to_new_request_fields(state_obj, exported_components);
            pivot_chart_utils.set_field_path_values_fetched_dictionary(state_obj, array_of_new_fields, 1);
            var success_func, complete_func;
            var error_func = function(error) {
                pivot_chart_utils.set_field_path_values_fetched_dictionary(state_obj, array_of_new_fields, 0);
                return props_obj.error_function(error);
            };
            if (run_immediately) {
                state_obj.last_successful_request = null;
                state_obj.last_exported_components = null;
                if(last_successful_request && pivot_chart_utils.compare_request_equality(last_successful_request, this_request)) { //If it's the same as the last request, then just cancel out
                    state_obj.last_exported_components = exported_components;
                    state_obj.last_successful_request = last_successful_request;
                    return pivot_chart_utils.resume_pivot_grid_updates(state_obj, props_obj, requires_column_reset);
                }
                success_func = function (data) {
                    if (data && data.data) {
                        state_obj.last_successful_request = this_request;
                        var aggregation_data = data.data || [];
                        var pivot_table_data = pivot_chart_utils.initialize_pivot_grid_columns_and_data(state_obj, props_obj, aggregation_data, state_obj.last_successful_request.exported_rows, state_obj.last_successful_request.exported_columns, state_obj.last_successful_request.exported_data, state_obj.last_successful_request.exported_filters, true, true, false, true);
                        if(props_obj.session_storage_code) {
                            try {
                                sessionStorage[props_obj.session_storage_code] = JSON.stringify({
                                    preset_columns_obj: pivot_chart_utils.make_create_aggregation_send_data(state_obj.last_successful_request),
                                    initial_data_source: pivot_table_data
                                });
                            }
                            catch(ex) {

                            }
                        }
                        state_obj.last_pivot_table_data = pivot_table_data;
                        state_obj.grid_properties.dataSource.store = pivot_table_data;
                        requires_column_reset = true;
                    }
                };
                complete_func = function () {
                    pivot_chart_utils.resume_pivot_grid_updates(state_obj, props_obj, requires_column_reset);
                };
            }
            else {
                aggregation_name = prompt(R.label_name_custom_aggregation || "Please enter a name for this aggregation", shared_model_utils.make_aggregation_chart_title(props_obj.entity_attributes, state_obj.last_successful_request.exported_data, state_obj.last_successful_request.exported_columns, state_obj.last_successful_request.exported_rows, function(a) {return R[a];}));
                if(aggregation_name == null) {
                    return;
                }
                success_func = function (data) {
                    Notify(data.message, 'bottom-right', notify_timeout, 'green', 'fa-check', true);
                    localStorage.removeItem('home');
                    localStorage.removeItem('get_home_layout'); //Refresh Dashboard Parts data
                };
                complete_func = function () {
                };
            }
            var send_data = pivot_chart_utils.make_create_aggregation_send_data(this_request, {
                entity_name: props_obj.entity_attributes.entity,
                export_grid_conditions: props_obj.export_grid_conditions,
                run_immediately: run_immediately,
                array_of_force_expanded_ref_fields: props_obj.array_of_force_expanded_ref_fields,
                aggregation_name: aggregation_name,
                is_private: !run_immediately //TODO: aaa Allow org-level and personal-level aggregations
            });
            invoke_method("aggregation", "create_aggregation_from_pivot_table", send_data, success_func, error_func, complete_func);
        }
        else if(run_immediately) {
            if(props_obj.session_storage_code) {
                sessionStorage.removeItem(props_obj.session_storage_code);
            }
            pivot_chart_utils.resume_pivot_grid_updates(state_obj, props_obj, requires_column_reset);
        }
    },
    initialize_pivot_grid_columns_and_data: function(state_obj, props_obj, aggregation_data, exported_rows, exported_columns, exported_data, exported_filters, reset_columns, data_requires_processing, force_set_column_areas, skip_set_column_areas) {
        var pivot_table_data;
        var do_set_column_areas = force_set_column_areas;
        if(aggregation_data && aggregation_data.length && exported_data && exported_data.length) {
            do_set_column_areas = true;
            if(data_requires_processing) {
                pivot_table_data = aggregation_data.filter(function (a) {
                    return a._id;
                }).map(function (a) {
                    for (var i = 0; i < exported_data.length; i++) {
                        a._id[exported_data[i].dataField] = a[exported_data[i].dataField]; //The aggregation has the group-by fields in _id, but the resulting aggregation totals on the object above.
                    }
                    pivot_chart_utils.add_null_date_values_to_record(state_obj, a._id);

                    return a._id;
                });
            }
            else {
                pivot_table_data = aggregation_data;
            }

            if (reset_columns && state_obj.columns && state_obj.columns.length) {
                for (var i = 0; i < state_obj.columns.length; i++) {
                    if (state_obj.columns[i]) {
                        delete state_obj.columns[i]._initProperties; //These are added by dx, and they are "sticky", so we remove them here before re-declaring grid.
                        delete state_obj.columns[i].area; //also keep these from sticking
                    }
                }
            }
        }

        if(!skip_set_column_areas && do_set_column_areas) {
            pivot_chart_utils.set_column_areas_for_pivot_grid(state_obj, exported_rows);
            pivot_chart_utils.set_column_areas_for_pivot_grid(state_obj, exported_columns);
            pivot_chart_utils.set_column_areas_for_pivot_grid(state_obj, exported_data);
            pivot_chart_utils.set_column_areas_for_pivot_grid(state_obj, exported_filters);
            //state_obj.pie_chart_series = pivot_grid_pie_chart_utils.make_pie_chart_series(exported_rows.concat(exported_columns), exported_data);
        }

        return pivot_table_data || [];
    },
    /**
     * Splits and sorts the dxPivotGrid save information into component categories that we will use to form our aggregation
     * @param exported_components
     * @param area_name
     * @returns {*}
     */
    make_processed_array_for_exporting_pivot_grid: function(exported_components, area_name) {
        var exported_array = exported_components.filter(function (a) {
            return (a.area && (a.area == area_name));
        });
        if((area_name == "filters") && exported_array.length) {
            exported_array = exported_array.filter(function(a) {
                return (a.filterValues && a.filterValues.length);
            });
        }
        exported_array = general_utils.get_unique_values_using_tostring_function(exported_array, function(a) {
            return a.dataField || "undefined";
        }, false);
        sort_by_field_name(exported_array, "areaIndex", {new_array: false});
        return exported_array;
    },
    /**
     * Used to initialize dxPivotGrid column settings to match what is saved in our own data, and also needed after server results return
     * @param state_obj
     * @param exported_components_array
     */
    set_column_areas_for_pivot_grid: function(state_obj, exported_components_array) {
        exported_components_array = exported_components_array || [];
        var columns_dictionary = state_obj.columns_dictionary || {}; //by dataField
        for(var i = 0; i < exported_components_array.length; i++) {
            var this_component = exported_components_array[i];
            if(this_component && this_component.dataField) {
                var this_column = columns_dictionary[this_component.dataField];
                if(this_column) {
                    this_column.area = this_component.area;
                    this_column.areaIndex = this_component.areaIndex;
                    this_column.summaryType = this_component.summaryType;
                    this_column.filterValues = this_component.filterValues;
                }
            }
        }
    },
    /**
     * Very simple equality comparisons used to determine if a new request is needed or if we can just harmlessly close the field_chooser and return to the data that is already there
     * @param last_successful_request
     * @param this_request
     * @returns {boolean}
     */
    compare_request_equality: function(last_successful_request, this_request) {
        if(last_successful_request && this_request) {
            if(pivot_chart_utils.compare_component_equality(last_successful_request.exported_rows, this_request.exported_rows)) {
                if(pivot_chart_utils.compare_component_equality(last_successful_request.exported_columns, this_request.exported_columns)) {
                    if(pivot_chart_utils.compare_component_equality(last_successful_request.exported_data, this_request.exported_data)) {
                        if(pivot_chart_utils.compare_component_equality(last_successful_request.exported_filters, this_request.exported_filters)) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    },
    /**
     * A helper function for compare_request_equality.  Simple equality comparisons.
     * @param array_a
     * @param array_b
     * @returns {boolean}
     */
    compare_component_equality: function(array_a, array_b) {
        if(!array_a && !array_b) {
            return true;
        }
        if(!array_a || !array_b) {
            return false;
        }
        if(!array_a.length && !array_b.length) {
            return true;
        }
        if(array_a.length != array_b.length) {
            return false;
        }


        for(var i = 0; i < array_a.length; i++) {
            var a = array_a[i];
            var b = array_b[i];
            if(a && b) {
                if((a.dataField != b.dataField) || (a.areaIndex != b.areaIndex) || (a.area != b.area) || (a.summaryType != b.summaryType)) {
                    return false;
                }
                if(a.filterValues && b.filterValues && a.filterValues.length && b.filterValues.length) {
                    //check JSON.stringify equality
                    var json_a = JSON.stringify(a.filterValues);
                    var json_b = JSON.stringify(b.filterValues);
                    if(json_a != json_b) {
                        return false;
                    }
                }
                else if (a.filterValues && b.filterValues) {
                    if (a.filterValues.length || b.filterValues.length) {
                        return false;
                    }
                }
                else if (a.filterValues || b.filterValues) {
                    return false;
                }
            }
            else if (a || b) {
                return false;
            }
        }

        return true; //Equal as far as our algorithm is concerned
    },
    //All allowed aggregation types used natively in the dxPivotGrid
    aggregation_types: [{caption: R.label_column_chooser_sum || "Sum", value: "sum"}, {caption: R.label_column_chooser_min || "Min", value: "min"}, {caption: R.label_column_chooser_max || "Max", value: "max"}, {caption: R.label_column_chooser_ave || "Avg", value: "avg"}], //Also accepts: "count" and "custom", but "count" is too confusing for numeric fields (and offers negligible benefit)
    /**
     * Uses Jquery to add a dxSelectBox which allows the user to change the aggregation summaryType used on a single field
     * @param state_obj
     * @param array_of_data_fields
     */
    add_fields_chooser_data_area_aggregation_type_selectors: function(state_obj, array_of_data_fields) {
        if(array_of_data_fields && array_of_data_fields.length) {
            array_of_data_fields = sort_by_field_name(array_of_data_fields, "areaIndex", {new_array: false});
            $(".dx-overlay-wrapper.dx-fieldchooser-popup.dx-popup-wrapper .dx-area-fields[group='data'] .dx-area-field-container .dx-area-field.dx-area-box .dx-area-field-content").each(function (index) {
                if(array_of_data_fields[index]) {
                    if ((array_of_data_fields[index].summaryType != "count") && (array_of_data_fields[index].dataField != "__record_count")) { //Types that are allowed to aggregate on "count" are ONLY allowed to aggregate on "count".
                        var data_field_selector = $(this).children('div[data-aggregation-type-list="true"]');
                        if (data_field_selector.length == 0) {
                            var tmpDiv = $("<span id='aggregation-type-list' data-aggregation-type-list='true'/>");
                            tmpDiv.dxSelectBox({
                                dataSource: pivot_chart_utils.aggregation_types,
                                displayExpr: "caption",
                                valueExpr: "value",
                                value: (array_of_data_fields[index].summaryType || pivot_chart_utils.aggregation_types[0].value),
                                height: "35px",
                                width: "175px",
                                onValueChanged: function (field_object) {
                                    return function (e) {
                                        pivot_chart_utils.switch_field_aggregation_type(state_obj, field_object, e.value);
                                    };
                                }(array_of_data_fields[index])
                            });
                            $(this).append(tmpDiv);
                        }
                    }
                }
            });
        }
    },
    /**
     * Changes the summaryType value for a column in dxPivotGrid's dataSource
     * @param state_obj
     * @param field_object
     * @param new_value
     */
    switch_field_aggregation_type: function(state_obj, field_object, new_value) {
        if(field_object && new_value) {
            field_object.summaryType = new_value;
            state_obj.pivot_grid.getDataSource().field(field_object.dataField, {
                summaryType: new_value
            });
            state_obj.pivot_grid.getDataSource().load();
        }
    },
    resize_func: function(state_obj, props_obj, size_obj) {
        var chart_height;
        var current_grid_visibility;

        if(state_obj.pivot_grid_chart_disabled) {
            chart_height = props_obj.update_height_function(-36, true, size_obj.height); //36 accounts for the height of the controls div
        }
        else {
            state_obj.chart_height = (size_obj.height / 2) || state_obj.chart_height;
            current_grid_visibility = state_obj.pivot_grid.option("visible");
            if (current_grid_visibility) {
                chart_height = state_obj.chart_height;
            }
            else {
                chart_height = props_obj.update_height_function(0, true, size_obj.height);
            }
        }
        state_obj.pivot_grid_chart.option("size.height", chart_height);
        state_obj.pivot_grid_chart.render();
        if(current_grid_visibility) {
            state_obj.pivot_grid.repaint();
        }
    },
    fetch_fields_chooser_data_filter_values_and_update_data_source: function(state_obj, props_obj, exported_components) {
        if(!state_obj.pivot_grid_chart_disabled || !exported_components || !exported_components.length) { //This function is only for use within the dxFieldChooser (state_obj.pivot_grid_chart_disabled)
            return;
        }
        var array_of_new_fields = pivot_chart_utils.filter_pivot_data_grid_to_new_request_fields(state_obj, exported_components);
        if(array_of_new_fields.length) {
            var agg_data = pivot_chart_utils.make_request_object(exported_components);

            agg_data.exported_rows = agg_data.exported_rows.concat(agg_data.exported_filters); //Move filters to a field where their values will be returned
            delete agg_data.exported_filters;

            var send_data = pivot_chart_utils.make_create_aggregation_send_data(agg_data, {
                entity_name: props_obj.entity_attributes.entity,
                export_grid_conditions: props_obj.export_grid_conditions,
                run_immediately: true,
                array_of_force_expanded_ref_fields: props_obj.array_of_force_expanded_ref_fields,
                ignore_filters: true
            });
            var success_func = function (data) {
                if (data && data.data && data.data.length) {
                    var aggregation_data = data.data.filter(function(a){return a._id;}).map(function(a) {
                        pivot_chart_utils.add_null_date_values_to_record(state_obj, a._id);
                        return a._id;
                    });

                    state_obj.data_filter_values = state_obj.data_filter_values.concat(aggregation_data);
                    pivot_chart_utils.swap_pivot_grid_aggregation_data_source_with_data_filter_source(state_obj, props_obj, true);
                }
            };
            var error_func = function(error) {
                pivot_chart_utils.set_field_path_values_fetched_dictionary(state_obj, array_of_new_fields, 0); //Unmark these values as fetched, since request failed
                return props_obj.error_function(error);
            };
            var complete_func = function() {
                setTimeout(function() {
                    pivot_chart_utils.toggle_data_loading(state_obj, false); //Remove Jquery data-loading screen
                }, 200);
            };
            pivot_chart_utils.set_field_path_values_fetched_dictionary(state_obj, array_of_new_fields, 1); //Mark these values as already fetched to prevent multiple such requests from going out
            pivot_chart_utils.toggle_data_loading(state_obj, true); // Display Jquery data-loading screen
            invoke_method("aggregation", "create_aggregation_from_pivot_table", send_data, success_func, error_func, complete_func);
        }
    },
    swap_pivot_grid_aggregation_data_source_with_data_filter_source: function(state_obj, props_obj, do_resume_and_disable_logic) {
        var local_state = state_obj.pivot_grid.getDataSource().state();
        state_obj.pivot_grid.option("dataSource.store", state_obj.data_filter_values);
        state_obj.pivot_grid.getDataSource().state(local_state);
        if(do_resume_and_disable_logic && state_obj.pivot_grid_chart_disabled) {
            state_obj.pivot_grid.option("visible", true);
            state_obj.pivot_grid.getFieldChooserPopup().show().then(function() { //This callback fires way to soon this time (not sure why), so we have to add a setTimeout
                setTimeout(function() {
                    pivot_chart_utils.do_post_field_chooser_popup_jquery_operations(state_obj, props_obj);
                }, 1000);
            });
            state_obj.pivot_grid.option("visible", false);
        }
    },
    toggle_data_loading: function(state_obj, is_loading) {
        if(is_loading && state_obj.dx_load_panel) {
            state_obj.dx_load_panel.show();
        }
        else {
            state_obj.dx_load_panel.hide();
        }
    },
    make_request_object: function(exported_components) {
        return {
            exported_rows: pivot_chart_utils.make_processed_array_for_exporting_pivot_grid(exported_components, "row"),
            exported_columns: pivot_chart_utils.make_processed_array_for_exporting_pivot_grid(exported_components, "column"),
            exported_data: pivot_chart_utils.make_processed_array_for_exporting_pivot_grid(exported_components, "data"),
            exported_filters: pivot_chart_utils.make_processed_array_for_exporting_pivot_grid(exported_components, "filter")
        };
    },
    /**
     * A helper method for making the aggregation request (maps fields from this_request field-names to server-side (and local stored) field-names
     * @param this_request
     * @param send_data_fields - Has the following fields:
                                 entity_name - The entity for which the aggregation is running
                                 export_grid_conditions - Conditions applied to all drilldowns and pivot-grid aggregations
                                 run_immediately - If false, then aggregation is saved to a dashboard part
                                 array_of_force_expanded_ref_fields - Value comes from a like-named field on entity_attributes
                                 aggregation_name - Names the aggregation (for saving), may be empty.
                                 is_private - Private or org-level
     * @returns {{y_axis_fields, applied_filters, x_axis_fields, chart_color_keys}}
     */
    make_create_aggregation_send_data: function(this_request, send_data_fields) {
        var ret_val = {
            y_axis_fields: this_request.exported_data,
            applied_filters: this_request.exported_filters,
            x_axis_fields: this_request.exported_columns,
            chart_color_keys: this_request.exported_rows
        };
        if(send_data_fields) {
            $.extend(ret_val, send_data_fields);
        }

        return ret_val;
    },
    filter_pivot_data_grid_to_new_request_fields: function(state_obj, exported_components) {
        if(!exported_components || !exported_components.length) {
            return [];
        }
        var array_of_filter_field_paths = exported_components.filter(function(a) {return (((a.area == "row") || (a.area == "column") || (a.area == "filter")) && (a.areaIndex >= 0));});
        return array_of_filter_field_paths.filter(shared_model_utils.pivot_grid_data_filter_and_map_function)
            .map(shared_model_utils.pivot_grid_data_filter_and_map_function)
            .filter(function(field_path) {
                return !state_obj.field_path_values_fetched_dictionary[field_path];
            });
    },
    set_field_path_values_fetched_dictionary: function(state_obj, array_of_new_fields, new_value) {
        for(var i = 0; i < array_of_new_fields.length; i++) {
            var local_field_path = array_of_new_fields[i];
            state_obj.field_path_values_fetched_dictionary[local_field_path] = new_value;
        }
    },
    /**
     * A small helper function for setting Date fields to null:  Date fields break the dxPivotGrid auto-generated filter if they are left as undefined (need to set to null).
     * @param state_obj
     * @param row_data
     */
    add_null_date_values_to_record: function (state_obj, row_data) {
        for(var i = 0; i < state_obj.date_fields_array_for_bug_workaround.length; i++) {
            var value = get_json_data_using_field_paths(state_obj.date_fields_array_for_bug_workaround[i], row_data);
            set_json_data_using_field_paths(state_obj.date_fields_array_for_bug_workaround[i], row_data, ((value) ? value : null), false, false, null, false);
        }
    },
    componentWillUnmount: function(state_obj) {
        state_obj.dx_tooltip.hide();
    },
    add_column_customize_text_and_selector_values: function(state_obj, props_obj, attribute, column) {
        if(attribute.entity_reference || attribute.entity_reference_variant) { //Add custom selectors for ref field captions
            column.customizeText = function (rowData) {
                if(rowData.value) {
                    if(rowData.value.caption) {
                        return rowData.value.caption;
                    }
                    else if ((typeof rowData.value == "string") && (state_obj.entity_ref_dictionary && state_obj.entity_ref_dictionary[rowData.value])) {
                        return state_obj.entity_ref_dictionary[rowData.value].caption;
                    }
                }
                if((rowData.valueText == null) || rowData.valueText == "") {
                    return R.label_chart_series_no_value || "(None)";
                }
                return rowData.valueText;
            };
            column.selector = function (rowData) {
                var value = get_json_data_using_field_paths(column.dataField, rowData);
                if (value) {
                    if (state_obj.entity_ref_dictionary && value.entity_id) {
                        state_obj.entity_ref_dictionary[value.entity_id] = value;
                    }
                    return value.entity_id;
                }
                return value;
            };
        }
    }
};

//A set of utils used specifically for allowing pie_chart graphing
//(Currently disabled)
var pivot_grid_pie_chart_utils = {
    create_pie_chart_display: function(state_obj, props_obj) {
        if(state_obj.pie_chart_series) {
            if (state_obj.unbind_pivot_grid_chart_function) {
                state_obj.unbind_pivot_grid_chart_function();
                state_obj.unbind_pivot_grid_chart_function = null;
            }
            props_obj.chart_section_jquery_selector.empty(); //Remove old charts from DOM

            var pivotGridChart = props_obj.chart_section_jquery_selector.dxPieChart({
                dataSource: pivot_grid_pie_chart_utils.make_pie_chart_series_from_data_source_data(state_obj.pivot_grid.getDataSource().getData()),
                series: state_obj.pie_chart_series,
                commonSeriesSettings: {
                    label: {
                        visible: true
                    }
                },
                size: {
                    height: state_obj.chart_height
                },
                adaptiveLayout: {
                    width: '100%',
                },
                onPointClick: function (e) {
                    var conditions_holder = e.target.tag;
                    if (conditions_holder) {
                        var app_object = {};
                        app_object.type = "data_list";
                        app_object.code = props_obj.entity_attributes.entity + "_default_data_list";
                        app_object.conditions = conditions_holder.conditions;
                        return props_obj.app_object_handler(app_object);
                    }
                }
            }).dxPieChart("instance");
        }
    },
    make_pie_chart_series: function(series_data_argumentField_array, series_data_valueField_array) {
        if(!series_data_argumentField_array || !series_data_argumentField_array.length || !series_data_valueField_array || !series_data_valueField_array.length) {
            return null;
        }
        if(series_data_argumentField_array.length > 1) {
            return null;
        }
        var argumentField = series_data_argumentField_array[0].dataField;
        return series_data_valueField_array.map(function(a, i) {
            return {
                argumentField: "argumentField",
                valueField: "valueField_" + i//a.dataField
            };
        });
    },
    make_pie_chart_series_from_data_source_data: function(data_source_data) {
        if(!data_source_data) {
            return null;
        }
        var rows = data_source_data.rows;
        var values = data_source_data.values;
        if(!values || !values.length) {
            return null;
        }

        if(!rows || !rows.length) {
            rows = data_source_data.columns;
            values = values[0];
        }

        if((values.length == 1) || !rows || !rows.length) {
            return null;
        }

        return rows.map(function(a) {
            var ret_val = {
                argumentField: a.text,
            };
            var values_array = values[a.index]; //first element in array is Total values.
            if(values_array && values_array.length) {
                for(var i = 0; i < values_array.length; i++) {
                    if(values_array[i] != null) {
                        ret_val["valueField_" + i] = values_array[i];
                    }
                }
            }

            return ret_val;
        });
    }
};
